/**
 * Two-tier coloring for Analyst / IC target cells.
 *
 *   light – Decision is "Add below ..." or "Reduce above ...".
 *   dark  – Light condition AND the row has an actionable suggestion
 *           (≥ 1 entry with a non-null trade_type) AND CMP has crossed the
 *           rec price (CMP ≤ price for "add"; CMP ≥ price for "reduce").
 *
 * Any other combination falls back to light.
 */

export type TargetSuggestion =
  | Record<string, { trade_type?: string | null; [key: string]: unknown }>
  | null
  | undefined;

type Side = "add" | "reduce";

const decisionSide = (decision: string | null | undefined): Side | null => {
  if (!decision) return null;
  const d = decision.toLowerCase();
  if (d.includes("add")) return "add";
  if (d.includes("reduce")) return "reduce";
  return null;
};

const hasActionableSuggestion = (suggestion: TargetSuggestion): boolean => {
  if (!suggestion) return false;
  return Object.values(suggestion).some((s) => s?.trade_type != null);
};

const isPriceTriggered = (
  side: Side,
  decisionPrice: number | null | undefined,
  currentPrice: number | null | undefined,
): boolean => {
  if (decisionPrice == null || currentPrice == null) return false;
  return side === "add"
    ? currentPrice <= decisionPrice
    : currentPrice >= decisionPrice;
};

/**
 * Returns the CSS class name (matching the CSS module) for a target cell, or
 * "" when no tier applies. The caller looks this up via `styles[returnedKey]`.
 */
export const getTargetCellClass = (
  decision: string | null | undefined,
  suggestion: TargetSuggestion,
  decisionPrice: number | null | undefined,
  currentPrice: number | null | undefined,
): string => {
  const side = decisionSide(decision);
  if (side == null) return "";

  if (
    hasActionableSuggestion(suggestion)
    && isPriceTriggered(side, decisionPrice, currentPrice)
  ) {
    return `rec-triggered-${side}`; // dark
  }

  return `target-${side}`; // light
};

/**
 * Variant for the Model column in the Implied Return section.
 *
 *   light – Model decision is "Add below ..." or "Reduce above ...".
 *   dark  – Light condition AND at least one of the provided sibling
 *           suggestions (analyst / IC) is actionable AND CMP has crossed the
 *           relevant model rec price:
 *             "Add below ..."   → CMP ≤ buyBelow
 *             "Reduce above ..." → CMP ≥ targetPrice
 *
 * Model has no suggestion field of its own, so dark is gated on the
 * analyst / IC suggestions instead.
 */
export const getModelCellClass = (
  decision: string | null | undefined,
  buyBelow: number | null | undefined,
  targetPrice: number | null | undefined,
  currentPrice: number | null | undefined,
  siblingSuggestions: TargetSuggestion[] = [],
): string => {
  const side = decisionSide(decision);
  if (side == null) return "";

  const decisionPrice = side === "add" ? buyBelow : targetPrice;
  const anySuggestion = siblingSuggestions.some(hasActionableSuggestion);
  if (anySuggestion && isPriceTriggered(side, decisionPrice, currentPrice)) {
    return `rec-triggered-${side}`; // dark
  }
  return `target-${side}`; // light
};
